import { SHORTFALLS } from '../data/resources.js';

/**
 * Who a thing belongs to, and what happens when it stops being where they need
 * it.
 *
 * An event pulls its par when you accept it. From that moment every round,
 * chair and linen you draw off the shelf is counted against that event — the
 * department has written them down against a room. Carry one out of the room
 * and abandon it in a corridor and the room is short by one, which is not a
 * punishment for anything, it is arithmetic.
 *
 * This is deliberately not a sabotage feature. There is no verb for ruining
 * somebody's setup, no target, and nothing anywhere asks whether you meant it.
 * There is one rule — a claimed object that comes to rest outside the room it
 * was claimed for is missing — and it fires exactly the same way whether you
 * threw a chair at a manager, dropped a linen on the way back from the walk-in,
 * or knocked a loaded cart over in a doorway and left it.
 *
 * The recovery is the same arithmetic. Bring it back into the room and the
 * shortfall shrinks. Nobody has to forgive you.
 */

/** Which goods shortfall a missing unit of each kind lands on. */
const SHORT_FOR = Object.freeze(
  Object.fromEntries(
    Object.values(SHORTFALLS)
      .filter((entry) => entry.kind === 'goods' && entry.item)
      .map((entry) => [entry.item, entry.id])
  )
);

/**
 * And which staging line each of the two carryable staging things settles.
 *
 * Staging is the same rule read the other way up. A goods shortfall counts what
 * is *missing* from the room, so a linen at rest in the corridor adds one; a
 * staging line is a single thing that is either in the room or is not, so a
 * riser at rest in the ballroom meets it and a riser at rest anywhere else does
 * not. Both are the one sentence this module exists for — a claimed thing is
 * where it came to rest — and neither of them asks how it got there.
 */
const STAGING_FOR = Object.freeze(
  Object.fromEntries(
    Object.values(SHORTFALLS)
      .filter((entry) => entry.kind === 'staging' && entry.item)
      .map((entry) => [entry.item, entry.id])
  )
);

export function createClaims({ shortfallsFor, inRoom }) {
  /** Units of each kind currently adrift, per event. */
  const adrift = new Map();

  const key = (owner, kind) => `${owner}:${kind}`;

  /**
   * Adds or removes the shortfall a missing unit implies.
   *
   * Uses the same entry shape the generated shortfalls use, so everything
   * downstream — the sheet, the board, asking somebody to cover it — treats a
   * linen you left in the corridor exactly like a linen Housekeeping never
   * sent. Which is what it is.
   */
  function apply(owner, kind, delta) {
    const id = SHORT_FOR[kind];
    if (!id) return null;
    const list = shortfallsFor(owner);
    if (!list) return null;

    const at = key(owner, kind);
    const count = Math.max(0, (adrift.get(at) ?? 0) + delta);
    adrift.set(at, count);

    let entry = list.find((item) => item.id === id);
    if (!entry) {
      if (count <= 0) return null;
      entry = { id, amount: 0, resolved: false, via: null, fromLoss: 0 };
      list.push(entry);
    }

    // Only the part of the shortfall this module is responsible for moves. What
    // Housekeeping did or did not send is not ours to shrink.
    const was = entry.fromLoss ?? 0;
    entry.fromLoss = count;
    entry.amount = Math.max(0, (entry.amount ?? 0) - was + count);
    if (entry.amount > 0) {
      entry.resolved = false;
      entry.via = null;
    } else if (was > 0) {
      entry.resolved = true;
      entry.via = 'recovered';
    }
    return entry;
  }

  /** A staging line is met by the thing being in the room, and by nothing else. */
  function stand(owner, kind, home) {
    const id = STAGING_FOR[kind];
    const list = shortfallsFor(owner);
    const entry = list?.find((item) => item.id === id);
    if (!entry) return null;
    entry.delivered = home;
    return entry;
  }

  return {
    /**
     * One object's position changed. Called for anything with a claim on it,
     * and it does not care how it got there.
     */
    settle(entity) {
      if (!entity?.claim) return null;
      const home = inRoom(entity.position);
      if (STAGING_FOR[entity.kind]) {
        entity.adrift = !home;
        return stand(entity.claim, entity.kind, home);
      }
      const was = entity.adrift ?? false;
      if (home === !was) return null;
      entity.adrift = !home;
      return apply(entity.claim, entity.kind, home ? -1 : 1);
    },

    /**
     * Back in the system.
     *
     * Something in a pair of hands is not missing, it is in transit — so a
     * claimed object stops counting the moment somebody picks it up, wherever
     * they are standing. Which is also what makes the rule fair: the way to
     * undo it is to go and get the thing, and going and getting the thing is
     * the whole of the cost.
     */
    recover(entity) {
      if (!entity?.claim) return null;
      // Picked back up is not delivered. A board in your arms is a board on its
      // way somewhere, and the sheet should say so until you put it down again.
      if (STAGING_FOR[entity.kind]) {
        entity.adrift = false;
        return stand(entity.claim, entity.kind, false);
      }
      if (!entity.adrift) return null;
      entity.adrift = false;
      return apply(entity.claim, entity.kind, -1);
    },

    /** How many units of a kind an event is currently missing because of this. */
    missing: (owner, kind) => adrift.get(key(owner, kind)) ?? 0,

    clear() {
      adrift.clear();
    },
  };
}
